import { BusinessObjectBase } from './base';
import { resourceSentenceSchema } from '@schemas/business-objects/resource-sentence';
import type { Resource } from './resource';
import type { Id } from '../base-types';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ResourceSentenceData {
  readonly id: Id;
  readonly sentence?: string;
  readonly word?: string;
  readonly sequence?: number;
  readonly resourceId: Id;
  readonly resource?: Resource;
  readonly locked?: boolean;
}

export interface ResourceSentence extends Readonly<ResourceSentenceData> {}
type ResourceSentenceCreateWithChildrenData = Partial<ResourceSentenceData>;

export class ResourceSentence extends BusinessObjectBase {
  static readonly entityName = 'ResourceSentence' as const;
  static readonly plural = 'resource-sentences';
  static schema = resourceSentenceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ResourceSentence[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ResourceSentence(data));
  }

  static async findOne(filter?: any): Promise<ResourceSentence | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ResourceSentence(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ResourceSentence> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ResourceSentence(data);
  }

  static async create(
    data: Omit<ResourceSentenceData, 'id'>,
  ): Promise<ResourceSentence> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ResourceSentence(result);
  }

  static async createMany(
    data: Omit<ResourceSentenceData, 'id'>[],
  ): Promise<ResourceSentence[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceSentence(item));
  }

  static async createWithChildren(
    data: ResourceSentenceCreateWithChildrenData,
  ): Promise<ResourceSentence> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ResourceSentence(result);
  }

  static async createManyWithChildren(
    data: ResourceSentenceCreateWithChildrenData[],
  ): Promise<ResourceSentence[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceSentence(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ResourceSentenceData>,
  ): Promise<ResourceSentence> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ResourceSentence(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async export(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSentence.plural}/${(this as any).id}/export`;
    return BusinessObjectBase.get<any>(url);
  }
  static async exportMany(body: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/export-many`;
    return this.post<any>(url, body);
  }
  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSentence.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
